import React, { useState, useEffect, useRef } from 'react';
import { Lock, Mail, Loader } from 'lucide-react';
import api from '../services/api';
import { safeNext } from '../utils/safeNext';

const NEXT_KEY = 'loginNext'; // the page they asked for, kept while they are away at an SSO provider

// Where to go once signed in: the page they asked for, else their home.
const destinationFor = (user, next) => safeNext(next) || (user?.status === 'PRE_JOINING' ? '/welcome' : '/dashboard');

const clearStaleSession = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
};

const saveNext = (next) => {
    try { if (next) sessionStorage.setItem(NEXT_KEY, next); else sessionStorage.removeItem(NEXT_KEY); } catch { /* storage blocked: they land on the default page */ }
};
const takeSavedNext = () => {
    try { const v = sessionStorage.getItem(NEXT_KEY); sessionStorage.removeItem(NEXT_KEY); return v; } catch { return null; }
};

const Login = () => {
    const [params] = useState(() => new URLSearchParams(window.location.search));
    // Arriving from the new-joiner invite page: email filled in, a short note shown.
    const [email, setEmail] = useState(() => params.get('email') || '');
    const [welcome] = useState(() => params.get('welcome') === '1');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [, setConfigLoading] = useState(true);
    const [error, setError] = useState('');
    // The browser's "signed in" flag can outlive the server session, so when it
    // is set we ask the server before deciding between the dashboard and this form.
    // People who followed a sign-in link on purpose (invite, SSO error) skip the check.
    const [checking, setChecking] = useState(() =>
        localStorage.getItem('isAuthenticated') === 'true' && !params.get('welcome') && !params.get('email') && !params.get('error'));
    const [config, setConfig] = useState({
        orgLogoUrl: '',
        primaryAuthMethod: 'MANUAL', // Default fallback
        googleAuthConfigured: false,
        microsoftAuthConfigured: false,
        samlAuthConfigured: false,
        orgName: ''
    });

    const arrived = useRef(false);

    useEffect(() => {
        // Once per arrival. React StrictMode runs effects twice in development, and a second
        // run would find the saved SSO destination already taken and send them to the dashboard.
        if (arrived.current) return;
        arrived.current = true;
        fetchSystemConfig();
        const next = params.get('next');

        // 1. Handle SSO success (the server sends the browser back to / with the user)
        const authStatus = params.get('auth');
        const userData = params.get('user');

        if (authStatus === 'success' && userData) {
            try {
                const user = JSON.parse(decodeURIComponent(userData));
                localStorage.setItem('isAuthenticated', 'true');
                localStorage.setItem('user', JSON.stringify(user));
                // replace: keep the user data in this address out of the back button
                window.location.replace(destinationFor(user, takeSavedNext() || next));
                return;
            } catch (err) {
                console.error('Failed to parse user data from SSO:', err);
                setError('Authentication succeeded but user data was malformed.');
            }
        }

        // 2. Handle Errors
        const errorParam = params.get('error');
        if (errorParam === 'saml_not_configured') {
            setError('SAML SSO is not configured yet. Please contact your administrator.');
        } else if (errorParam === 'auth_failed') {
            setError('SSO authentication failed. Please try again or use email/password.');
        } else if (errorParam === 'auth_error') {
            const message = params.get('message');
            setError(message || 'Authentication error occurred.');
        }

        // 3. Already signed in? Go straight in; a stale flag is cleared and the form shown.
        if (checking) {
            api.get('/auth/session')
                .then((res) => {
                    // An unknown /api path answers 200 with the app's own page, so look for the user itself.
                    const user = res.data?.user;
                    if (!user) { clearStaleSession(); setChecking(false); return; }
                    const stored = JSON.parse(localStorage.getItem('user') || '{}');
                    localStorage.setItem('user', JSON.stringify({ ...stored, ...user }));
                    window.location.replace(destinationFor(user, next));
                })
                .catch((err) => {
                    // 401/403: not (or no longer) signed in here. Anything else (offline, 5xx): keep the flag, show the form.
                    if (err.response?.status === 401 || err.response?.status === 403) clearStaleSession();
                    setChecking(false);
                });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once, on arrival
    }, []);

    const fetchSystemConfig = async () => {
        try {
            const response = await api.get('/auth/config');
            setConfig(response.data);
        } catch (err) {
            console.error('Failed to fetch system config', err);
        } finally {
            setConfigLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');

        if (!email || !password) {
            setError('Please enter both email and password');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/auth/login', { email, password });

            // Login Successful
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.setItem('user', JSON.stringify(response.data.user));

            // Use window.location for more reliable navigation in tests
            window.location.href = destinationFor(response.data.user, params.get('next'));
        } catch (err) {
            console.error('Login Failed:', err);
            setError(err.response?.data?.error || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    // Leave for the identity provider, remembering where they were headed.
    const startSso = (provider) => {
        saveNext(safeNext(params.get('next')));
        window.location.href = `${api.defaults.baseURL}/auth/${provider}`;
    };

    if (checking) {
        return (
            <div role="status" aria-label="Checking your session" style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', color: 'var(--text-muted)',
            }}>
                <Loader size={28} className="animate-spin" />
            </div>
        );
    }

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            padding: '1rem'
        }}>
            <div style={{
                background: 'var(--surface)',
                padding: '2.5rem',
                borderRadius: '1rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                width: '100%',
                maxWidth: '400px',
                marginBottom: '1rem'
            }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
                    <img src={config.orgLogoUrl || "/logo.png"} alt={config.orgName} style={{ height: '100px', objectFit: 'contain' }} />
                </div>
                <h2 style={{ textAlign: 'center', marginBottom: '2rem', color: 'var(--text-secondary)' }}>{config.orgName} Login</h2>

                {welcome && !error && (
                    <div role="status" style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 12, background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>Your password is set. Sign in to open your joining portal.</div>
                )}
                {error && (
                    <div style={{
                        background: '#fee2e2',
                        color: '#b91c1c',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        marginBottom: '1rem',
                        fontSize: '0.9rem',
                        textAlign: 'center'
                    }}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleLogin}>
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>Email</label>
                        <div style={{ position: 'relative' }}>
                            <Mail size={20} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                                    borderRadius: '0.5rem',
                                    border: '1px solid var(--border)',
                                    fontSize: '1rem'
                                }}
                                placeholder="name@company.com"
                                required
                            />
                        </div>
                    </div>
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>Password</label>
                        <div style={{ position: 'relative' }}>
                            <Lock size={20} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                                    borderRadius: '0.5rem',
                                    border: '1px solid var(--border)',
                                    fontSize: '1rem'
                                }}
                                placeholder="Enter password"
                                required
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: loading ? '#93c5fd' : 'var(--primary, #4f46e5)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'background 0.2s',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        {loading && <Loader size={18} className="animate-spin" />}
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>

                    {/* Divider for SSO */}
                    {(config.googleAuthConfigured || config.microsoftAuthConfigured || config.samlAuthConfigured) && (
                        <div style={{ display: 'flex', alignItems: 'center', margin: '1.5rem 0' }}>
                            <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
                            <span style={{ padding: '0 1rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>OR</span>
                            <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
                        </div>
                    )}

                    {/* Google Sign-In */}
                    {config.googleAuthConfigured && (
                        <button
                            type="button"
                            onClick={() => startSso('google')}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: 'var(--surface)',
                                color: 'var(--text-secondary)',
                                border: '1px solid var(--border)',
                                borderRadius: '0.5rem',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '0.75rem',
                                marginBottom: '0.75rem'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                            <svg width="18" height="18" viewBox="0 0 18 18">
                                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
                                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
                                <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.593.102-1.17.282-1.709V4.958H.957C.347 6.173 0 7.548 0 9c0 1.452.348 2.827.957 4.042l3.007-2.335z" />
                                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
                            </svg>
                            Sign in with Google
                        </button>
                    )}

                    {/* Microsoft Sign-In */}
                    {config.microsoftAuthConfigured && (
                        <button
                            type="button"
                            onClick={() => startSso('microsoft')}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: 'var(--surface)',
                                color: 'var(--text-secondary)',
                                border: '1px solid var(--border)',
                                borderRadius: '0.5rem',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '0.75rem'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                            <svg width="18" height="18" viewBox="0 0 23 23">
                                <path fill="#f3f3f3" d="M0 0h23v23H0z" />
                                <path fill="#f35325" d="M1 1h10v10H1z" />
                                <path fill="#81bc06" d="M12 1h10v10H12z" />
                                <path fill="#05a6f0" d="M1 12h10v10H1z" />
                                <path fill="#ffba08" d="M12 12h10v10H12z" />
                            </svg>
                            Sign in with Microsoft
                        </button>
                    )}

                    {/* SAML SSO Sign-In */}
                    {config.samlAuthConfigured && (
                        <button
                            type="button"
                            onClick={() => startSso('saml')}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: 'var(--surface)',
                                color: 'var(--text-secondary)',
                                border: '1px solid var(--border)',
                                borderRadius: '0.5rem',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '0.75rem',
                                marginTop: '0.75rem'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            Login with SSO
                        </button>
                    )}

                    <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                    </div>
                </form>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                JA Technology Labs © 2026 | Version 2.0.0
            </p>
        </div>
    );
};

export default Login;
